import { Col, Form, Row } from "react-bootstrap";
import { CompanyType, SocialIconStyle, socialIconStyles } from "../../models/company";

interface CompanySocialsProps {
  company: CompanyType;
  originalCompany: CompanyType | null;
  setCompany: (company: CompanyType) => void;
  hasFieldChanged: (currentValue: any, originalValue: any) => boolean;
  ChangedIndicator: () => JSX.Element;
  readOnly?: boolean;
}

type SocialKey = "facebook_url" | "instagram_url" | "linkedin_url" | "youtube_url";

const SOCIAL_FIELDS: { key: SocialKey; label: string; placeholder: string }[] = [
  { key: "facebook_url", label: "Facebook", placeholder: "https://www.facebook.com/yourpage" },
  { key: "instagram_url", label: "Instagram", placeholder: "https://www.instagram.com/yourpage" },
  { key: "linkedin_url", label: "LinkedIn", placeholder: "https://www.linkedin.com/company/yourpage" },
  { key: "youtube_url", label: "YouTube", placeholder: "https://www.youtube.com/@yourchannel" },
];

// SVG path data for each social icon
const ICON_PATHS: Record<SocialKey, string> = {
  facebook_url: "M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z",
  instagram_url:
    "M7.8 2h8.4C19.4 2 22 4.6 22 7.8v8.4a5.8 5.8 0 01-5.8 5.8H7.8C4.6 22 2 19.4 2 16.2V7.8A5.8 5.8 0 017.8 2zm-.2 2A3.6 3.6 0 004 7.6v8.8C4 18.39 5.61 20 7.6 20h8.8a3.6 3.6 0 003.6-3.6V7.6C20 5.61 18.39 4 16.4 4H7.6zm9.65 1.5a1.25 1.25 0 110 2.5 1.25 1.25 0 010-2.5zM12 7a5 5 0 110 10 5 5 0 010-10zm0 2a3 3 0 100 6 3 3 0 000-6z",
  linkedin_url: "M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-4 0v7h-4v-7a6 6 0 016-6zM2 9h4v12H2zM4 2a2 2 0 110 4 2 2 0 010-4z",
  youtube_url:
    "M22.54 6.42a2.78 2.78 0 00-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 00-1.94 2A29 29 0 001 11.75a29 29 0 00.46 5.33A2.78 2.78 0 003.4 19.1c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 001.94-2 29 29 0 00.46-5.25 29 29 0 00-.46-5.43zM9.75 15.02V8.48l5.75 3.27-5.75 3.27z",
};

// Bootstrap icon class for the form labels only (React UI, not email)
const LABEL_ICONS: Record<SocialKey, string> = {
  facebook_url: "bi-facebook",
  instagram_url: "bi-instagram",
  linkedin_url: "bi-linkedin",
  youtube_url: "bi-youtube",
};

const DEFAULT_PRIMARY_BG = "#0049ff";
const DEFAULT_PRIMARY_TEXT = "#ffffff";
const DEFAULT_SECONDARY_BG = "#ffffff";
const DEFAULT_SECONDARY_TEXT = "#1a2236";

function isFilled(style: SocialIconStyle): boolean {
  return style === "rounded-square" || style === "circle" || style === "square";
}

/** Inline SVG icon rendered as a data-URI <img> */
function SocialIcon({
  fieldKey,
  color,
  size = 20,
  outlined = false,
}: {
  fieldKey: SocialKey;
  color: string;
  size?: number;
  outlined?: boolean;
}) {
  const encodedColor = encodeURIComponent(color);
  const svg = outlined
    ? `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="${encodedColor}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="${ICON_PATHS[fieldKey]}"/></svg>`
    : `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="${encodedColor}" stroke="none"><path d="${ICON_PATHS[fieldKey]}"/></svg>`;
  return <img src={`data:image/svg+xml,${svg}`} alt="" width={size} height={size} style={{ display: "block" }} />;
}

/** Get container styles for a given icon shape */
function getContainerStyle(
  style: SocialIconStyle,
  size: number,
  fillColor: string,
  borderColor: string,
): React.CSSProperties {
  const base: React.CSSProperties = {
    width: size,
    height: size,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  switch (style) {
    case "rounded-square":
      return { ...base, borderRadius: size * 0.22, backgroundColor: fillColor };
    case "rounded-square-outlined":
      return {
        ...base,
        borderRadius: size * 0.22,
        backgroundColor: "transparent",
        border: `1.5px solid ${borderColor}`,
      };
    case "circle":
      return { ...base, borderRadius: "50%", backgroundColor: fillColor };
    case "circle-outlined":
      return { ...base, borderRadius: "50%", backgroundColor: "transparent", border: `1.5px solid ${borderColor}` };
    case "square":
      return { ...base, borderRadius: 0, backgroundColor: fillColor };
    case "square-outlined":
      return { ...base, borderRadius: 0, backgroundColor: "transparent", border: `1.5px solid ${borderColor}` };
    case "minimal":
      return { ...base };
    case "minimal-outlined":
      return { ...base };
  }
}

function CompanySocials({
  company,
  originalCompany,
  setCompany,
  hasFieldChanged,
  ChangedIndicator,
  readOnly = false,
}: CompanySocialsProps) {
  const socials = company.config_design_temp?.socials ?? {};
  const originalSocials = originalCompany?.config_design_temp?.socials ?? {};

  const primaryBg = company.config_design_temp?.colors?.primary?.background || DEFAULT_PRIMARY_BG;
  const primaryText = company.config_design_temp?.colors?.primary?.text || DEFAULT_PRIMARY_TEXT;
  const secondaryBg = company.config_design_temp?.colors?.secondary?.background || DEFAULT_SECONDARY_BG;
  const secondaryText = company.config_design_temp?.colors?.secondary?.text || DEFAULT_SECONDARY_TEXT;

  const iconStyle: SocialIconStyle = socials.icon_style ?? "rounded-square";

  const updateSocial = (key: string, value: string) => {
    setCompany({
      ...company,
      config_design_temp: {
        ...company.config_design_temp,
        socials: {
          ...socials,
          [key]: value || undefined,
        },
      },
    });
  };

  const updateIconStyle = (style: SocialIconStyle) => {
    setCompany({
      ...company,
      config_design_temp: {
        ...company.config_design_temp,
        socials: {
          ...socials,
          icon_style: style,
        },
      },
    });
  };

  const hasAnyUrl = SOCIAL_FIELDS.some((f) => socials[f.key]);

  /** Render a row of social icons for a given background context */
  const renderIconRow = (
    fields: typeof SOCIAL_FIELDS,
    style: SocialIconStyle,
    bgColor: string,
    textColor: string,
    size: number,
  ) => (
    <div style={{ display: "flex", justifyContent: "center", gap: 10 }}>
      {fields.map((sf) => (
        <div key={sf.key} style={getContainerStyle(style, size, textColor, textColor)}>
          <SocialIcon
            fieldKey={sf.key}
            color={isFilled(style) ? bgColor : textColor}
            size={Math.round(size * 0.55)}
            outlined={style === "minimal-outlined"}
          />
        </div>
      ))}
    </div>
  );

  /** Render a single style card */
  const renderStyleCard = (style: (typeof socialIconStyles)[number]) => {
    const isSelected = iconStyle === style.id;
    return (
      <div
        onClick={() => !readOnly && updateIconStyle(style.id)}
        style={{
          border: isSelected ? "2px solid #0049ff" : "1px solid #dee2e6",
          borderRadius: "8px",
          overflow: "hidden",
          cursor: readOnly ? "default" : "pointer",
          transition: "border-color 0.15s",
          height: "100%",
        }}
      >
        {/* On primary background */}
        <div style={{ backgroundColor: primaryBg, padding: "10px 8px" }}>
          {renderIconRow(SOCIAL_FIELDS, style.id, primaryBg, primaryText, 32)}
        </div>
        {/* On secondary background */}
        <div style={{ backgroundColor: secondaryBg, padding: "10px 8px" }}>
          {renderIconRow(SOCIAL_FIELDS, style.id, secondaryBg, secondaryText, 32)}
        </div>
        {/* Label */}
        <div style={{ padding: "8px 12px", textAlign: "center", backgroundColor: "#fff" }}>
          <div style={{ fontSize: "0.8rem", fontWeight: isSelected ? 600 : 400 }}>{style.label}</div>
          <div style={{ fontSize: "0.7rem", color: "#6c757d", marginTop: 2 }}>{style.description}</div>
        </div>
      </div>
    );
  };

  // Split styles into rows
  const topRow = socialIconStyles.filter((s) => s.row === 0);
  const bottomRow = socialIconStyles.filter((s) => s.row === 1);

  return (
    <Form>
      {/* ── Social URLs ──────────────────────────────────────────── */}
      <Row>
        {SOCIAL_FIELDS.map((field) => {
          const currentValue = socials[field.key] ?? "";
          const originalValue = originalSocials[field.key] ?? "";
          const isChanged = hasFieldChanged(currentValue, originalValue);

          return (
            <Col md={6} key={field.key} className="mb-3">
              <Form.Group>
                <Form.Label style={{ fontSize: "0.85rem" }}>
                  <i className={`bi ${LABEL_ICONS[field.key]} me-1`}></i>
                  {field.label}
                  {isChanged && <ChangedIndicator />}
                </Form.Label>
                <Form.Control
                  type="url"
                  value={currentValue}
                  onChange={(e) => updateSocial(field.key, e.target.value)}
                  placeholder={field.placeholder}
                  readOnly={readOnly}
                />
              </Form.Group>
            </Col>
          );
        })}
      </Row>

      <hr />

      {/* ── Icon Style ───────────────────────────────────────────── */}
      <h6 className="mb-3">
        Icon Style
        {hasFieldChanged(socials.icon_style, originalSocials.icon_style) && <ChangedIndicator />}
      </h6>
      <p className="text-muted" style={{ fontSize: "0.8rem" }}>
        Choose the icon shape used in email footers. Icons use your primary and secondary colors.
      </p>

      {/*
        Grid layout:
        [ Rounded Square ] [ Circle ] [ Square ] [ Minimal          ]
        [ RS Outlined    ] [ C Out  ] [ Sq Out ] [ Minimal Outlined ]
      */}
      <div
        className="mb-4"
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr 1fr",
          gap: 12,
        }}
      >
        {topRow.map((style) => (
          <div key={style.id}>{renderStyleCard(style)}</div>
        ))}
        {bottomRow.map((style) => (
          <div key={style.id}>{renderStyleCard(style)}</div>
        ))}
      </div>

      {/* ── Preview ──────────────────────────────────────────────── */}
      {hasAnyUrl && (
        <>
          <hr />
          <h6 className="mb-3 text-muted" style={{ fontSize: "0.85rem" }}>
            Preview
          </h6>
          <div
            style={{
              border: "1px solid #dee2e6",
              borderRadius: "8px",
              overflow: "hidden",
            }}
          >
            {/* On secondary bg (typical email footer) */}
            <div style={{ backgroundColor: secondaryBg, padding: "20px", textAlign: "center" }}>
              <div style={{ display: "flex", justifyContent: "center", gap: 12 }}>
                {SOCIAL_FIELDS.filter((f) => socials[f.key]).map((field) => (
                  <a
                    key={field.key}
                    href={socials[field.key]}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: "none" }}
                  >
                    <div style={getContainerStyle(iconStyle, 40, secondaryText, secondaryText)}>
                      <SocialIcon
                        fieldKey={field.key}
                        color={isFilled(iconStyle) ? secondaryBg : secondaryText}
                        outlined={iconStyle === "minimal-outlined"}
                      />
                    </div>
                  </a>
                ))}
              </div>
              <div className="mt-1" style={{ fontSize: "0.65rem", color: secondaryText, opacity: 0.5 }}>
                on secondary background
              </div>
            </div>
            {/* On primary bg */}
            <div style={{ backgroundColor: primaryBg, padding: "20px", textAlign: "center" }}>
              <div style={{ display: "flex", justifyContent: "center", gap: 12 }}>
                {SOCIAL_FIELDS.filter((f) => socials[f.key]).map((field) => (
                  <a
                    key={field.key}
                    href={socials[field.key]}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: "none" }}
                  >
                    <div style={getContainerStyle(iconStyle, 40, primaryText, primaryText)}>
                      <SocialIcon
                        fieldKey={field.key}
                        color={isFilled(iconStyle) ? primaryBg : primaryText}
                        outlined={iconStyle === "minimal-outlined"}
                      />
                    </div>
                  </a>
                ))}
              </div>
              <div className="mt-1" style={{ fontSize: "0.65rem", color: primaryText, opacity: 0.5 }}>
                on primary background
              </div>
            </div>
          </div>
        </>
      )}
    </Form>
  );
}

export default CompanySocials;
